import { motion, AnimatePresence } from 'framer-motion';
import { X, Database, CheckCircle, Archive, Shield, TrendingUp } from 'lucide-react';

interface BackupArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BackupArchiveModal({ isOpen, onClose }: BackupArchiveModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-purple-500/30 shadow-2xl shadow-purple-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-purple-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                    <Database className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                      Backup & Archive Long Terme
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Un élément fort chez BZ - Peu de compétiteurs offrent cette solution
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 rounded-xl p-8 border-2 border-purple-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Backup & Archive : Notre Point Fort
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  Nos solutions de <strong className="text-white">backup avec archive long terme</strong> sont un 
                  <strong className="text-purple-400"> élément fort chez BZ</strong> que 
                  <strong className="text-pink-400"> peu de compétiteurs offrent</strong>. 
                  Protection complète de vos données avec accès à long terme.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Database className="w-7 h-7 text-purple-400" />
                    Solutions de Backup
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Backup complet et fiable de vos données :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Backup régulier</strong> - Sauvegardes automatisées</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Rétention multiple</strong> - Plusieurs points de restauration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Test de restauration</strong> - Validation régulière</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Monitoring</strong> - Surveillance continue des backups</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-xl p-6 border border-pink-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Archive className="w-7 h-7 text-pink-400" />
                    Archive Long Terme
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Archive long terme pour conservation et conformité :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Conservation longue durée</strong> - Données accessibles à long terme</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Conformité réglementaire</strong> - Respect des exigences de rétention</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Accès rapide</strong> - Récupération efficace même après plusieurs années</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Indexation</strong> - Recherche et localisation facilitées</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-indigo-500/10 to-blue-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-7 h-7 text-indigo-400" />
                    Sécurité des Données
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Protection complète de vos données archivées :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Chiffrement</strong> - Données protégées en transit et au repos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Intégrité</strong> - Vérification de l'intégrité des données</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Redondance</strong> - Copies multiples pour sécurité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Contrôles d'accès</strong> - Accès sécurisé et tracé</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-7 h-7 text-yellow-400" />
                    Avantage Concurrentiel
                  </h4>
                  <p className="text-gray-300">
                    Le backup et archive long terme est un <strong className="text-white">avantage concurrentiel</strong> de BZ Inc. 
                    <strong className="text-yellow-400"> Peu de compétiteurs offrent cette solution complète</strong>. 
                    C'est un différenciateur puissant qui rassure nos clients et répond à leurs besoins de protection et de conformité.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-purple-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












